import { useState } from 'react'

import { useParams } from 'react-router-dom'

import {
  useCancelProductUnderstandingRunApi,
  useProductUnderstandingEstimateApi,
  useProductUnderstandingRunsApi,
  useStartProductUnderstandingRunApi
} from '@/common/api/modules/productUnderstanding'
import { PuRun } from '@/common/api/params/productUnderstanding'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import Table from '@/components/view/atoms/table/Table'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'

/** 進行中のときだけポーリングする（常時ポーリングは無駄な負荷になる） */
const POLL_MS = 5000

const TRIGGER_LABEL: Record<string, string> = {
  manual: '画面',
  schedule: '日次',
  cli: 'コマンド'
}

const STATUS_LABEL: Record<string, { text: string; color: TextColor }> = {
  running: { text: '実行中', color: TextColor.CAUTION },
  complete: { text: '完了', color: TextColor.SUCCESS },
  cancelled: { text: '中断', color: TextColor.SECONDARY },
  error: { text: '失敗', color: TextColor.CRITICAL }
}

/** 精度の但し書き。実績が無いサイトで黙って概算を出さない */
const ACCURACY_NOTE: Record<string, string> = {
  site: 'このサイトの実績から算出しています。',
  global: 'このサイトの実績がまだ無いため、他サイトの実績から算出しています。',
  fallback: '実績がまだ無いため固定値から算出しています。精度は低めです。'
}

const fmtDateTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('ja-JP', { dateStyle: 'short', timeStyle: 'short' }) : '—'

const fmtEta = (sec: number | null) => {
  if (sec === null) return null
  if (sec < 60) return `残り約 ${sec} 秒`
  const m = Math.round(sec / 60)
  return m < 60 ? `残り約 ${m} 分` : `残り約 ${Math.round(m / 60)} 時間`
}

/** 数値タイル。`Text` はインライン要素なので、行ごとに div で包まないと横に潰れる */
const Metric = ({ label, value, note }: { label: string; value: string; note?: string }) => (
  <div className='min-w-[160px]'>
    <Text size={12} color={TextColor.SECONDARY}>
      {label}
    </Text>
    <div>
      <Text size={24} weight='bold'>
        {value}
      </Text>
    </div>
    {note && (
      <div>
        <Text size={10} color={TextColor.TERTIARY}>
          {note}
        </Text>
      </div>
    )}
  </div>
)

const ProductUnderstandingRunSection = () => {
  const { siteId: siteIdParam } = useParams()
  const siteId = Number(siteIdParam)

  // **見積りは押したときだけ取りに行く。** 開いただけで実行できる状態にしない
  const [wantEstimate, setWantEstimate] = useState(false)
  const [acting, setActing] = useState(false)

  const { data: estimate, isLoading: estimating } = useProductUnderstandingEstimateApi(
    wantEstimate ? { site_id: siteId } : undefined
  )
  const { data: runs, mutate } = useProductUnderstandingRunsApi({ site_id: siteId }, POLL_MS)

  const running = runs?.running ?? null
  const done = () => {
    setActing(false)
    mutate()
  }
  const { trigger: start } = useStartProductUnderstandingRunApi({
    onSuccess: done,
    onError: () => setActing(false)
  })
  const { trigger: cancel } = useCancelProductUnderstandingRunApi({
    onSuccess: done,
    onError: () => setActing(false)
  })

  const canRun = !!estimate && estimate.pending > 0 && !running

  return (
    <div className='flex flex-col gap-4'>
      <Area>
        <Heading subText='AIが商品情報を読み取って意味を抽出します。費用が発生するため、実行前に必ず件数と概算をご確認ください。'>
          意味理解の実行
        </Heading>
        <AreaContent>
          {running ? (
            <div className='mt-3 flex flex-col gap-2'>
              <div>
                <Text size={13}>
                  実行中： {running.processed.toLocaleString()} /{' '}
                  {running.pending.toLocaleString()} 件
                </Text>
              </div>
              {/* 実績が出るまでETAを出さない。当てずっぽうの残り時間は害になる */}
              {fmtEta(running.eta_seconds) && (
                <div>
                  <Text size={12} color={TextColor.SECONDARY}>
                    {fmtEta(running.eta_seconds)}
                  </Text>
                </div>
              )}
              <div className='bg-secondary h-2 w-full overflow-hidden rounded-full'>
                <div
                  className='bg-brand h-2 rounded-full transition-all'
                  style={{
                    width: `${running.pending ? Math.min(100, (running.processed / running.pending) * 100) : 0}%`
                  }}
                />
              </div>
              <div>
                <Text size={12} color={TextColor.SECONDARY}>
                  ここまでの概算： ${running.cost_usd.toFixed(2)}
                </Text>
              </div>
              {running.cancel_requested ? (
                <div>
                  <Text size={12} color={TextColor.CAUTION}>
                    中断を受け付けました。処理中の区切りまで進んでから停止します。
                  </Text>
                </div>
              ) : (
                <div>
                  <Button
                    color={ButtonColor.SECONDARY}
                    disabled={acting}
                    loading={acting}
                    onClick={() => {
                      setActing(true)
                      cancel({ site_id: siteId, run_id: running.id })
                    }}
                  >
                    中断する
                  </Button>
                </div>
              )}
            </div>
          ) : !estimate ? (
            <div className='mt-3 flex flex-col gap-2'>
              <div>
                <Text size={12} color={TextColor.SECONDARY}>
                  未処理の件数と概算費用を確認してから実行します。
                </Text>
              </div>
              <div>
                <Button
                  disabled={estimating}
                  loading={estimating}
                  onClick={() => setWantEstimate(true)}
                >
                  件数と概算を確認する
                </Button>
              </div>
            </div>
          ) : (
            <div className='mt-3 flex flex-col gap-3'>
              <div className='flex flex-wrap gap-x-10 gap-y-4'>
                <Metric
                  label='未処理'
                  value={`${estimate.pending.toLocaleString()} 件`}
                  note={`新規 ${estimate.new.toLocaleString()} / 変更 ${estimate.changed.toLocaleString()} / 情報が少なく対象外 ${estimate.thin.toLocaleString()}`}
                />
                <Metric
                  label='AIが読む商品'
                  value={`${estimate.billable.toLocaleString()} 件`}
                  note='情報が少ない商品は読まないため費用に含みません'
                />
                <Metric
                  label='概算費用'
                  value={`$${estimate.estimated_cost_usd.toFixed(2)}`}
                  note={`余裕を見た上限寄りの金額です（${estimate.margin} 倍）`}
                />
              </div>

              <div>
                <Text size={10} color={TextColor.TERTIARY}>
                  {ACCURACY_NOTE[estimate.accuracy]}
                  実際の費用は商品の説明文の長さによって上下します。
                </Text>
              </div>

              {estimate.pending === 0 ? (
                <div>
                  <Text size={13} color={TextColor.SUCCESS}>
                    すべて理解済みです。実行の必要はありません。
                  </Text>
                </div>
              ) : (
                <div className='flex flex-wrap gap-2'>
                  <Button
                    disabled={!canRun || acting}
                    loading={acting}
                    onClick={() => {
                      setActing(true)
                      start({ site_id: siteId })
                    }}
                  >
                    この内容で実行する
                  </Button>
                  <Button
                    color={ButtonColor.SECONDARY}
                    disabled={acting}
                    onClick={() => setWantEstimate(false)}
                  >
                    やり直す
                  </Button>
                </div>
              )}
            </div>
          )}
        </AreaContent>
      </Area>

      <Area>
        <Heading subText='画面・日次バッチ・コマンドのいずれから実行しても、ここに記録が残ります。'>
          実行履歴
        </Heading>
        <AreaContent>
          {!runs || runs.items.length === 0 ? (
            <div className='mt-2'>
              <Text size={13} color={TextColor.SECONDARY}>
                まだ実行の記録がありません
              </Text>
            </div>
          ) : (
            <div className='mt-3'>
              <Table>
                <thead>
                  <TableRow head>
                    <TableHeadItem>
                      <Text size={12}>開始</Text>
                    </TableHeadItem>
                    <TableHeadItem>
                      <Text size={12}>経路</Text>
                    </TableHeadItem>
                    <TableHeadItem>
                      <Text size={12}>状態</Text>
                    </TableHeadItem>
                    <TableHeadItem>
                      <Text size={12}>処理</Text>
                    </TableHeadItem>
                    <TableHeadItem>
                      <Text size={12}>トークン</Text>
                    </TableHeadItem>
                    <TableHeadItem>
                      <Text size={12}>概算費用</Text>
                    </TableHeadItem>
                  </TableRow>
                </thead>
                <tbody>
                  {runs.items.map((r: PuRun) => {
                    const st = STATUS_LABEL[r.status] ?? {
                      text: r.status,
                      color: TextColor.SECONDARY
                    }
                    return (
                      <TableRow key={r.id}>
                        <TableRowItem>
                          <Text size={12} nowrap>
                            {fmtDateTime(r.started_at)}
                          </Text>
                        </TableRowItem>
                        <TableRowItem>
                          <Text size={12} nowrap>
                            {TRIGGER_LABEL[r.trigger] ?? r.trigger}
                          </Text>
                        </TableRowItem>
                        <TableRowItem>
                          <Text size={12} color={st.color} nowrap>
                            {st.text}
                          </Text>
                          {r.error && (
                            <div>
                              <Text size={10} color={TextColor.TERTIARY}>
                                {r.error.slice(0, 40)}
                              </Text>
                            </div>
                          )}
                        </TableRowItem>
                        <TableRowItem>
                          <Text size={12} nowrap>
                            {r.processed.toLocaleString()} 件
                          </Text>
                          {r.deferred > 0 && (
                            <div>
                              <Text size={10} color={TextColor.TERTIARY} nowrap>
                                上限のため {r.deferred.toLocaleString()} 件は次回へ
                              </Text>
                            </div>
                          )}
                        </TableRowItem>
                        <TableRowItem>
                          <Text size={12} nowrap>
                            {(r.prompt_tokens + r.completion_tokens).toLocaleString()}
                          </Text>
                        </TableRowItem>
                        <TableRowItem>
                          <Text size={12} nowrap>
                            ${r.cost_usd.toFixed(2)}
                          </Text>
                        </TableRowItem>
                      </TableRow>
                    )
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </AreaContent>
      </Area>
    </div>
  )
}

export default ProductUnderstandingRunSection
